import React, { Component } from 'react';
import { connect } from 'react-redux';
import Axios from 'axios';
import { Capacitor } from '@capacitor/core';
import i18n from '../../../../i18next';
import { Col, Form, Input, FormGroup, FormFeedback } from "reactstrap";
import { isIOS, isMacOs, isMobileOnly, isSafari } from 'mobile-device-detect';

import { placeOrder } from '../../../../actions';
import { SERVER_URL, MONERIS_LINK, FRONT_END_URL, ARABIC } from '../../../../utils/Constants';
import { getQueryInfoFromUrl, getAllUrlParams } from '../../../../utils/CommonUtils';

class MonerisForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isSaveCardAllowed: false,
            dataKey: '',
            street: '',
            number: '',
            postCode: '',
            streetError: false,
            numberError: false,
            postCodeError: false,
            monerisFrameReady: false
        }
        this._monerisIabLoadStartHandler = null;
        this._monerisFrameReadyRef = false;
        this._monerisAuthorizeDeferTimer = null;
    }

    componentDidMount = () => {
        this.props.validateCardInput(true);
        window.addEventListener('message', this.respMsg, false);
        const paymentReturn = new URLSearchParams(window.location.search).get('success');
        if (paymentReturn !== null) {
            this.completeOrder();
        }
    }

    componentWillUnmount = () => {
        window.removeEventListener('message', this.respMsg, false);
        if (this._monerisAuthorizeDeferTimer) {
            clearTimeout(this._monerisAuthorizeDeferTimer);
            this._monerisAuthorizeDeferTimer = null;
        }
    }

    componentDidUpdate = (prevProps) => {
        if (prevProps.payment.isCardValid !== this.props.payment.isCardValid) {
            //Moneris does not allow us to know if card field is filled or not. 
            //Instead they return error codes which we have already handled for this we set the isCardValid property to always be True
            //and therefore the place order button will always be active
            this.props.validateCardInput(true);
        }
        const prevProfileId = prevProps.paymentProvider.monerisPremenuData && prevProps.paymentProvider.monerisPremenuData.profileId;
        const nextProfileId = this.props.paymentProvider.monerisPremenuData && this.props.paymentProvider.monerisPremenuData.profileId;
        if (prevProfileId !== nextProfileId) {
            this._monerisFrameReadyRef = false;
            this.setState({ monerisFrameReady: false });
        }
    }

    changeState = ({ target }) => {
        if (target.name === 'isSaveCardAllowed') {
            this.setState({
                isSaveCardAllowed: !this.state.isSaveCardAllowed
            });
        } else {
            this.setState({
                [target.name]: target.value,
                [`${target.name}Error`]: target.value ? false : true
            });
        }
    }

    respMsg = (e) => {
        let response = {};
        try {
            response = JSON.parse(e.data);
        } catch (catchError) {
            // console.log(catchError)
        }
        // const isBillingAddressEntered = this.state.street !== '' && this.state.number !== '' && this.state.postCode !== '';    // if street and number are required

        if (response.responseCode) {
            if (response.responseCode[0] === '001') {
                this.setState({ dataKey: response.dataKey }, () => this.doPlaceOrder());
            } else {
                this.props.errorProcessingPayment(response.responseCode, '', this.props.paymentProvider.providerType.id);
            }
        }
    }

    /** Same notion as redirect URL: native Capacitor must match backend `app` flag and InAppBrowser 3DS path. */
    isAppShell = () => this.props.pwaAppRunning || Capacitor.isNativePlatform();

    /**
     * WKWebView (device + simulator) often fails the first XHR right after a tap/postMessage tokenize;
     * a short defer fixes "first authorize fails, second succeeds" for new and saved cards. Android: 0 ms.
     */
    getMonerisAuthorizeDeferMs = () => {
        if (!this.isAppShell()) {
            return 0;
        }
        const onIos = Capacitor.getPlatform() === 'ios' || isIOS;
        return onIos ? 160 : 0;
    }

    doPlaceOrder = async () => {
        const { payment } = this.props;
        const selectedCard = payment.selectedPaymentCard;
        const usingSavedCard = !!(selectedCard && selectedCard.paymentCardId);
        const monerisDataKey = usingSavedCard ? selectedCard.paymentCardId : this.state.dataKey;

        if (!monerisDataKey) {
            this.props.errorProcessingPayment([], '', this.props.paymentProvider.providerType.id);
            return;
        }

        const deferMs = this.getMonerisAuthorizeDeferMs();
        if (deferMs > 0) {
            if (this._monerisAuthorizeDeferTimer) {
                clearTimeout(this._monerisAuthorizeDeferTimer);
            }
            this._monerisAuthorizeDeferTimer = setTimeout(() => {
                this._monerisAuthorizeDeferTimer = null;
                void this.executeMonerisAuthorize();
            }, deferMs);
            return;
        }

        await this.executeMonerisAuthorize();
    }

    executeMonerisAuthorize = async () => {
        const { customer, selectedRestaurant, shoppingCart, payment } = this.props;
        const selectedCard = payment.selectedPaymentCard;
        const usingSavedCard = !!(selectedCard && selectedCard.paymentCardId);
        const monerisDataKey = usingSavedCard ? selectedCard.paymentCardId : this.state.dataKey;

        if (!monerisDataKey) {
            this.props.errorProcessingPayment([], '', this.props.paymentProvider.providerType.id);
            return;
        }

        const shoppingCartTotalWithGiftCard = this.props.giftCards.giftCards ? this.props.giftCards.giftCards.total : null;
        let paymentIntentRequest = {
            customerId: customer.id,
            restaurantId: selectedRestaurant.restaurantId,
            amount: shoppingCartTotalWithGiftCard ? shoppingCartTotalWithGiftCard.toFixed(2) : shoppingCart.total,
            currency: selectedRestaurant.currency,
            app: this.isAppShell(),
            monerisAuthorizeRequest: {
                dataKey: monerisDataKey,
                redirectUrl: this.getMenuuRedirectUrl(),
                challengeWindowSize: '05',
                userAgent: window.navigator.userAgent,
                browserLang: window.navigator.language,
                windowHeight: window.innerHeight,
                windowWidth: window.innerWidth,
                newCard: usingSavedCard ? false : this.state.isSaveCardAllowed,
                street: usingSavedCard ? selectedCard.street : this.state.street,
                streetNumber: usingSavedCard ? selectedCard.streetNumber : this.state.number,
                zipCode: usingSavedCard ? selectedCard.zipCode : this.state.postCode
            }
        }

        const header = { headers: { 'Authorization': `TOKEN${customer.token}` } };

        await Axios.post(`${SERVER_URL}/payment/authorize`, paymentIntentRequest, header)
            .then(response => {
                if (!response.data.monerisResponse.challangeUrl) {   // normal flow
                    const providerData = {
                        dataKey: response.data.monerisResponse.dataKey,
                        orderId: response.data.monerisResponse.orderId,
                        cavv: response.data.monerisResponse.cavv,
                        txnNumber: response.data.monerisResponse.txnNumber,
                        newCard: usingSavedCard ? false : this.state.isSaveCardAllowed,
                        amount: shoppingCartTotalWithGiftCard ? shoppingCartTotalWithGiftCard.toFixed(2) : shoppingCart.total,
                        street: response.data.monerisResponse.street,
                        streetNumber: response.data.monerisResponse.streetNumber,
                        zipCode: response.data.monerisResponse.zipCode
                    };

                    this.props.placeOrder(false, providerData);
                } else {    //3d secure flow                                
                    this.secureRedirect(response.data.monerisResponse);
                }
            })
            .catch(error => {
                this.props.errorProcessingPayment(error, 'Authorize Failure', this.props.paymentProvider.providerType.id);
                this.props.sendEmailReport(selectedRestaurant.restaurantId, error, 'paymentIntentFailure');
            });
    }

    getMenuuRedirectUrl = () => {
        const { navigationType, brandId } = this.props.storeLocalStorage;
        const isIntegration = getQueryInfoFromUrl().brandId;
        let redirectUrl = '';

        if (isIntegration) {
            redirectUrl = `${FRONT_END_URL}/?brandId=${brandId}&navigationType=${navigationType}#/checkout/`;
        } else if (this.isAppShell()) {
            redirectUrl = `${FRONT_END_URL}/payment-redirect.html`;
        } else {
            redirectUrl = `${FRONT_END_URL}/#/checkout`;
        }

        return redirectUrl;
    }

    secureRedirect = (response) => {
        let challangeUrl = response.challangeUrl;
        let challangeData = response.challangeData;
        let monerisForm = `<form id="monerisForm" method="POST" action="${challangeUrl}"><input type="hidden" name="creq" value="${challangeData}"></form>`;

        this.props.saveMonerisData(response);

        setTimeout(() => {
            if (this.isAppShell()) {
                const pageContent = `<html><body>${monerisForm}<script type="text/javascript">document.getElementById("monerisForm").submit();</script></body></html>`;
                const pageContentUrl = 'data:text/html;base64,' + btoa(pageContent);
                let browserRef = window.cordova.InAppBrowser.open(pageContentUrl, '_blank', 'location=no');
                this._monerisIabLoadStartHandler = (event) => this.completeOrder(event, browserRef);
                browserRef.addEventListener('loadstart', this._monerisIabLoadStartHandler);
            } else {
                document.body.insertAdjacentHTML('afterbegin', monerisForm);
                document.getElementById('monerisForm').submit();
            }
        }, 2000)
    }

    checkCardData = (e) => {
        e.preventDefault();
        this.props.processingPayment();
        if (!(this.props.payment.selectedPaymentCard && this.props.payment.selectedPaymentCard.paymentCardId)) {
            const postTokenize = () => {
                const frame = document.getElementById('monerisFrame');
                const monFrameRef = frame && frame.contentWindow;
                if (!monFrameRef) {
                    this.props.errorProcessingPayment([], '', this.props.paymentProvider.providerType.id);
                    return;
                }
                monFrameRef.postMessage('tokenize', MONERIS_LINK);
            };
            if (!this._monerisFrameReadyRef) {
                let attempts = 0;
                const maxAttempts = 50;
                const waitForMonerisFrame = () => {
                    if (this._monerisFrameReadyRef) {
                        postTokenize();
                        return;
                    }
                    attempts += 1;
                    if (attempts >= maxAttempts) {
                        this.props.errorProcessingPayment([], '', this.props.paymentProvider.providerType.id);
                        return;
                    }
                    setTimeout(waitForMonerisFrame, 100);
                };
                waitForMonerisFrame();
                return;
            }
            postTokenize();
        } else {
            this.doPlaceOrder();
        }
    }

    handleMonerisFrameLoad = () => {
        this._monerisFrameReadyRef = true;
        this.setState({ monerisFrameReady: true });
    }

    completeOrder = (event, browserRef) => {
        const shoppingCartTotalWithGiftCard = this.props.giftCards.giftCards ? this.props.giftCards.giftCards.total : null;
        if (new URLSearchParams(window.location.search).get('success') && this.props.paymentProvider.monerisData) {    // desktop flow
            const isSuccess = new URLSearchParams(window.location.search).get('success');

            if (isSuccess === 'true') {
                const providerData = {
                    dataKey: this.props.paymentProvider.monerisData.dataKey,
                    orderId: this.props.paymentProvider.monerisData.orderId,
                    cavv: new URLSearchParams(window.location.search).get('cavv'),
                    newCard: new URLSearchParams(window.location.search).get('isNewCard'),
                    amount: shoppingCartTotalWithGiftCard ? shoppingCartTotalWithGiftCard.toFixed(2) : this.props.shoppingCart.total,
                    street: this.props.paymentProvider.monerisData.street,
                    streetNumber: this.props.paymentProvider.monerisData.streetNumber,
                    zipCode: this.props.paymentProvider.monerisData.zipCode
                };

                this.props.placeOrder(false, providerData);
                this.props.clearMonerisData();
            } else {
                this.props.errorProcessingPayment([], '', this.props.paymentProvider.providerType.id);
                this.props.clearMonerisData();
            }
        } else if (event) {                                                    // apps flow
            if ((event.url).indexOf('success=') !== -1 && this.props.paymentProvider.monerisData) {
                browserRef.close();
                if (this._monerisIabLoadStartHandler) {
                    browserRef.removeEventListener('loadstart', this._monerisIabLoadStartHandler);
                    this._monerisIabLoadStartHandler = null;
                }
                let allUrlParams = getAllUrlParams(event.url);
                const isSuccess = allUrlParams.success;

                if (isSuccess === 'true') {
                    const providerData = {
                        dataKey: this.props.paymentProvider.monerisData.dataKey,
                        orderId: this.props.paymentProvider.monerisData.orderId,
                        cavv: allUrlParams.cavv,
                        newCard: allUrlParams.isNewCard,
                        amount: shoppingCartTotalWithGiftCard ? shoppingCartTotalWithGiftCard.toFixed(2) : this.props.shoppingCart.total,
                        street: this.props.paymentProvider.monerisData.street,
                        streetNumber: this.props.paymentProvider.monerisData.streetNumber,
                        zipCode: this.props.paymentProvider.monerisData.zipCode
                    }

                    this.props.placeOrder(false, providerData);
                    this.props.clearMonerisData();
                } else {
                    this.props.errorProcessingPayment([], '', this.props.paymentProvider.providerType.id);
                    this.props.clearMonerisData();
                }
            }
        } else if (this.props.paymentProvider.monerisData) {    // handle error when click back button or cancel transaction
            this.props.errorProcessingPayment([], '', this.props.paymentProvider.providerType.id);
            this.props.clearMonerisData();
        }
    }

    renderMonerisForm = () => {
        let { inputsFontColor, chefNoteBackground } = this.props.customerThemes.selectedTheme;
        let cardFieldsBackground = chefNoteBackground.replace('#', '');
        inputsFontColor = inputsFontColor.replace('#', '');
        const profileId = this.props.paymentProvider.monerisPremenuData.profileId;
        const isPwa = this.props.pwaAppRunning || isMobileOnly;
        let source = `${MONERIS_LINK}?id=${profileId}&pmmsg=true&css_body=background:${cardFieldsBackground};display:flex;align-items:center&css_textbox=background-color:${cardFieldsBackground};border:none;outline:none;color:${inputsFontColor};font-size:16px;padding:0px&css_textbox_pan=width:${isPwa ? 60 : 60}%;&enable_exp=1&css_textbox_exp=width:${isPwa ? 25 : 30}%;&enable_cvd=1&css_textbox_cvd=width:${isPwa ? 15 : 10}%;&display_labels=2${isPwa ? '&pan_label=Card Number&exp_label=MM/YY' : '&pan_label=Card Number&exp_label=Expiry Date MM/YY'}`

        return (
            <div style={{ display: 'flex' }}>
                <div className="checkout" id="card-element" style={{ backgroundColor: chefNoteBackground, height: 46, width: '100%', border: '1px solid lightgrey', padding: '12px 12px 12px' }}>
                    <iframe id='monerisFrame' src={source} frameBorder='0' width="100%" height="23px" onLoad={this.handleMonerisFrameLoad} title='Moneris card fields' />
                </div>
            </div>
        )
    }

    renderBillingAddress = () => {
        const { menuItemsBackground, inputsFontColor, chefNoteBackground } = this.props.customerThemes.selectedTheme;
        const { billingInputsStyle } = styles;
        const isMobileView = window.innerWidth <= 565;
        const isIOSApp = isIOS && this.props.pwaAppRunning ? true : false;

        return (
            <div style={{ display: 'flex', marginTop: 15, flexDirection: isMobileView ? 'column' : 'row' }}>
                <FormGroup style={{ width: isMobileView ? '100%' : '90%' }}>
                    <Input
                        className={isSafari || isIOSApp ? 'monerisStreet' : null}
                        type='text'
                        name='street'
                        placeholder={i18n.t('screens:checkoutScreen.cardBillingAddress')}
                        value={this.state.street}
                        // invalid={this.state.streetError}
                        onChange={this.changeState}
                        style={{ ...billingInputsStyle, backgroundColor: chefNoteBackground, color: inputsFontColor }}
                        required={false}/>
                    {/* <FormFeedback>{i18n.t('common:validations.errorMsg')}</FormFeedback> */}
                </FormGroup>
                <div style={{width: isMobileView ? '100%' : '40%', display: 'flex'}}>
                    <FormGroup style={!isMobileView ? { marginLeft: 10 } : null}>
                        <Input
                            className={isSafari || isIOSApp ? 'monerisNumber' : null}
                            type='text'
                            name='number'
                            placeholder={i18n.t('screens:checkoutScreen.streetNumber')}
                            value={this.state.number}
                            // invalid={this.state.numberError}
                            onChange={this.changeState}
                            style={{ ...billingInputsStyle, backgroundColor: chefNoteBackground, color: inputsFontColor }} />
                        {/* <FormFeedback>{i18n.t('common:validations.errorMsg')}</FormFeedback> */}
                    </FormGroup>
                    <FormGroup style={{ marginLeft: 10 }}>
                        <Input
                            className={isSafari || isIOSApp ? 'monerisPostCode' : null}
                            type='text'
                            name='postCode'
                            placeholder={i18n.t('screens:checkoutScreen.zipOrPostalCode')}
                            value={this.state.postCode}
                            // invalid={this.state.postCodeError}
                            onChange={this.changeState}
                            style={{ ...billingInputsStyle, backgroundColor: chefNoteBackground, color: inputsFontColor }} />
                        {/* <FormFeedback>{i18n.t('common:validations.errorMsg')}</FormFeedback> */}
                    </FormGroup>
                </div>
            </div>
        )
    }

    renderSaveCard = () => {
        let { primaryFontColor } = this.props.customerThemes.selectedTheme;
        const isArabic = this.props.language === ARABIC;

        return (
            <label
                htmlFor='isSaveCardAllowed'
                className='marketingCheckbox-label unselectable'
                style={{
                    minWidth: '90%',
                    margin: isArabic ? '5px 22px auto auto' : '5px auto auto 22px',
                    color: primaryFontColor,
                    fontSize: 16,
                    lineHeight: '33px',
                    cursor: this.props.payment.selectedPaymentCard.paymentCardId ? 'default' : 'pointer'
                }}>
                <span style={{ marginLeft: 10 }}>{i18n.t('screens:checkoutScreen.saveCardMsg')}</span>
                <input
                    type='checkbox' name='isSaveCardAllowed' id='isSaveCardAllowed' style={{ marginTop: 5 }}
                    checked={this.state.isSaveCardAllowed}
                    onChange={this.changeState} disabled={this.props.payment.selectedPaymentCard.paymentCardId} />
                <span className='marketingCheckbox-custom'></span>
            </label>
        )
    }

    render = () => {
        const { monerisElementStyle } = styles;
        let { primaryFontColor } = this.props.customerThemes.selectedTheme;

        return (
            <Col xs={12} sm={12} md={8} style={monerisElementStyle} hidden={this.props.paymentWithCard}>
                <Form
                    id='monerisForm'
                    onSubmit={(e) => this.checkCardData(e)}
                    hidden={this.props.payment.selectedPaymentType.id !== 1}
                >
                    {this.renderMonerisForm()}
                    {/* not needed for now */}
                    {this.renderBillingAddress()}
                    {this.renderSaveCard()}
                    <label style={{color: primaryFontColor}}>{i18n.t('screens:checkoutScreen.cardDetailsInfo')}</label>
                </Form>
            </Col>
        )
    }
}

export default connect(null, { placeOrder })(MonerisForm);

let styles = {

    monerisElementStyle: {
        margin: 'auto',
        paddingBottom: 10
    },

    billingInputsStyle: {
        borderRadius: 0,
        height: 46,
        padding: 12
    }
}


